import {
  RejectedRule,
  RunEvaluationResponse,
} from "@financial-advisory/data-access/api-types";
import { Outcome } from "@financial-advisory/data-access/api-types";
import { createSlice } from "@reduxjs/toolkit";
import { TruthValue } from "@unlikelyai-magic/shared/data-access/api";
import type { RootState } from "../store";
import { ProcessedClauseApi } from "./decisionProcesses";
import { setActivePaneId } from "./decisionProcessesSlice";

// Map backend TruthValue to frontend Outcome
const mapTruthValueToOutcome = (value: TruthValue): Outcome => {
  switch (value) {
    case "True":
      return Outcome.TRUE;
    case "False":
      return Outcome.FALSE;
    case "DontKnow":
      return Outcome.UNKNOWN;
    default:
      return Outcome.UNDECIDED;
  }
};

// Normalize TruthValue from backend to LeafData with Outcome
const buildLeafDataMaps = (
  values: Record<string, TruthValue>,
  explanations: Record<string, string>
): Record<string, LeafData> =>
  Object.entries(values).reduce(
    (acc, [key, value]) => {
      acc[key] = {
        value: mapTruthValueToOutcome(value),
        explanation: explanations[key] ?? "no source excerpt available",
      };
      return acc;
    },
    {} as Record<string, LeafData>
  );
// Define a type for the slice state
interface RegulationQueryState {
  activeQuery: string | null;
  lastActivePaneId: string | null;
  loading: boolean;
  error: boolean;
  leaves: Record<string, LeafData>;
  staticAssignments: Record<string, LeafData>;
  queryAssignments: Record<string, LeafData>;
  rejectedRules: RejectedRule[];
}

interface LeafData {
  value: Outcome;
  explanation: string;
}

export interface EvaluatedResponse {
  leaves: Record<string, TruthValue>;
  explanations: Record<string, string>;
}

// Define the initial state using that type
const initialState: RegulationQueryState = {
  activeQuery: null,
  lastActivePaneId: null,
  loading: false,
  error: false,
  leaves: {},
  staticAssignments: {},
  queryAssignments: {},
  rejectedRules: [],
};

const clearActiveQueryStateImplementation = (state: RegulationQueryState) => {
  state.activeQuery = null;
  state.loading = false;
  state.error = false;
  state.queryAssignments = {};
  state.leaves = { ...state.staticAssignments };
  state.rejectedRules = [];
};

export const regulationQuerySlice = createSlice({
  name: "regulationQuery",
  initialState,
  reducers: {
    clearActiveQueryState: clearActiveQueryStateImplementation,
    setStaticAssignments: (state, action: { payload: EvaluatedResponse }) => {
      state.staticAssignments = buildLeafDataMaps(
        action.payload.leaves,
        action.payload.explanations
      );
      const merged = {
        ...(state.queryAssignments || {}),
        ...(state.staticAssignments || {}),
      };
      state.leaves = merged;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(setActivePaneId, (state, action) => {
        // Clear query state when switching to a different pane
        if (action.payload !== state.lastActivePaneId) {
          state.lastActivePaneId = action.payload;
          clearActiveQueryStateImplementation(state);
        }
      })
      .addMatcher(
        ProcessedClauseApi.endpoints.evaluateQuery.matchPending,
        (state, action) => {
          state.loading = true;
          state.leaves = { ...state.staticAssignments };
          state.rejectedRules = [];
          state.error = false;
          state.activeQuery = action.meta.arg.originalArgs.query;
        }
      )
      .addMatcher(
        ProcessedClauseApi.endpoints.evaluateQuery.matchFulfilled,
        (state, action) => {
          const response: RunEvaluationResponse = action.payload;
          state.loading = false;
          state.error = false;
          const merged = {
            ...(buildLeafDataMaps(response.leaves, response.explanations) ||
              {}),
            ...(state.staticAssignments || {}),
          };
          state.leaves = merged;
          state.rejectedRules = response.rejectedRules || [];
        }
      )
      .addMatcher(
        ProcessedClauseApi.endpoints.evaluateQuery.matchRejected,
        (state, action) => {
          state.loading = false;
          state.rejectedRules = [];
          state.activeQuery = action.meta.arg.originalArgs.query;
          state.error = true;
        }
      );
  },
});

export const { clearActiveQueryState, setStaticAssignments } =
  regulationQuerySlice.actions;

export const selectActiveQueryState = (state: RootState) =>
  state.regulationQuery;
export const selectActiveQuery = (state: RootState) =>
  state.regulationQuery.activeQuery;
export const selectEvaluating = (state: RootState) =>
  state.regulationQuery.loading;
export const selectLeafValueByName = (name: string) => (state: RootState) =>
  state.regulationQuery.leaves?.[name]?.value;
export const selectLeafValueExplanationByName =
  (name: string) => (state: RootState) =>
    state.regulationQuery.leaves?.[name]?.explanation ??
    "no source excerpt available";
export const selectRejectedRules = (state: RootState) =>
  state.regulationQuery.rejectedRules;

export default regulationQuerySlice.reducer;
